"use client";

import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { motion } from "framer-motion";
import { FaCheck } from "react-icons/fa";
import { FiTarget } from "react-icons/fi";
import { missionData } from "@/data/main-data";
import { useGetAbout } from "@/hooks/main-hooks";

const MotionImage = motion(Image);

export default function Vision() {
  const t = useTranslations("AboutPage");
  const locale = useLocale();
  const isAr = locale === "ar";

  const { data: about } = useGetAbout();

  const visionTitle =
    (isAr ? about?.vision?.titleAr : about?.vision?.titleEn) || t("visionTitle");
  const visionDes =
    (isAr ? about?.vision?.descriptionAr : about?.vision?.descriptionEn) ||
    t("visionDes");
  const missionTitle =
    (isAr ? about?.mission?.titleAr : about?.mission?.titleEn) || t("mission");
  const missionDes =
    (isAr ? about?.mission?.descriptionAr : about?.mission?.descriptionEn) ||
    t("missionDes");

  return (
    <section className="relative py-20 sm:py-28 bg-white overflow-hidden">
      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center text-[22vw] font-black text-brand-navy/5 pointer-events-none select-none">
        {!isAr ? "VISION" : "الرؤية"}
      </div>

      <div className="container mx-auto px-5 sm:px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className={`mb-12 sm:mb-20 ${isAr ? "text-right" : "text-left"}`}>
          <span className="inline-block text-[10px] sm:text-xs tracking-[0.35em] uppercase text-brand-red font-bold">
            {isAr ? "من نحن" : "Who We Are"}
          </span>

          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-brand-navy tracking-tight leading-tight">
            {t("vision")} <span className="text-brand-blue">&</span>{" "}
            {t("mission")}
          </h2>

          <div
            className={`mt-4 h-[3px] w-20 bg-brand-red rounded-full ${isAr ? "ml-auto" : ""}`}
          />
        </div>

        <div className="space-y-24 sm:space-y-32">
          {/* Vision */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative bg-[#f8f7f2] rounded-4xl sm:rounded-[2.5rem] p-8 sm:p-12 lg:p-16 shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
            >
              <span className="absolute top-6 sm:top-8 right-6 sm:right-10 text-6xl sm:text-7xl font-black text-brand-navy/5 select-none">
                01
              </span>

              <h3 className="text-2xl sm:text-3xl font-black text-brand-navy mb-5">
                {visionTitle}
              </h3>

              <p className="text-brand-navy/70 leading-relaxed text-base sm:text-lg">
                {visionDes}
              </p>
            </motion.div>

            <div className="relative h-[280px] sm:h-[360px] lg:h-auto rounded-4xl sm:rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
              <MotionImage
                initial={{ scale: 1.15, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeOut" }}
                src="/TlsWW0xA.jpeg"
                alt="Vision"
                fill
                className="object-cover grayscale"
              />
              <div className="absolute inset-0 bg-brand-navy/25" />
            </div>
          </div>

          {/* Mission */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-stretch">
            <div className="relative h-[280px] sm:h-[360px] lg:h-auto rounded-4xl sm:rounded-4xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
              <MotionImage
                initial={{ scale: 1.15, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeOut" }}
                src="/ABTTuWrN.jpeg"
                alt="Mission"
                fill
                className="object-cover grayscale"
              />
              <div className="absolute inset-0 bg-brand-red/20" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative bg-brand-creamy text-brand-navy rounded-4xl sm:rounded-[2.5rem] p-8 sm:p-12 lg:p-16 shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
            >
              <span className="absolute top-6 sm:top-8 left-6 sm:left-10 text-6xl sm:text-7xl font-black text-white/5 select-none">
                02
              </span>

              <div className="flex items-center gap-4 mb-6">
                <span className="p-3 rounded-full bg-brand-red/10 text-brand-red">
                  <FiTarget className="text-2xl" />
                </span>
                <h3 className="text-2xl sm:text-3xl font-black">
                  {missionTitle}
                </h3>
              </div>

              <p className="text-brand-navy/70 leading-relaxed text-base sm:text-lg mb-10">
                {missionDes}
              </p>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {missionData.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 text-sm sm:text-base font-semibold text-brand-navy/90"
                  >
                    <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-brand-red flex items-center justify-center text-white shadow-sm">
                      <FaCheck size={11} />
                    </span>
                    <span className="leading-relaxed">
                      {isAr ? item.name_ar : item.name_en}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
